import React, { useEffect, useRef } from 'react';
import * as d3 from 'd3';
import '../../scss/Charts.scss';
import { formatQuarterLabel } from '../utils/formatQuarterLabel';

const HeatmapChart = ({ data, columns }) => {
    const containerRef = useRef();
    const svgRef = useRef();
    const tooltipRef = useRef();

    function positionTooltip(event) {
        const tooltipNode = tooltipRef.current;
        const containerRect = containerRef.current.getBoundingClientRect();
        const tooltipRect = tooltipNode.getBoundingClientRect();

        let left = event.clientX - containerRect.left + 10;
        let top = event.clientY - containerRect.top - 40;

        const viewportWidth = window.innerWidth;
        const viewportHeight = window.innerHeight;

        if (event.clientX + tooltipRect.width + 20 > viewportWidth) {
            left = event.clientX - containerRect.left - tooltipRect.width - 10;
        }
        
        if (event.clientY - tooltipRect.height - 20 < 0) {
            top = event.clientY - containerRect.top + 20;
        }
        
        if (event.clientY + tooltipRect.height + 20 > viewportHeight) {
            top = event.clientY - containerRect.top - tooltipRect.height - 10;
        }

        tooltipNode.style.left = `${left}px`;
        tooltipNode.style.top = `${top}px`;
    }

    useEffect(() => {
        if (!Array.isArray(data) || !data.length || !containerRef.current) return;

        const containerWidth = containerRef.current.getBoundingClientRect().width;
        const margin = {
            top: 20,
            right: 20,
            bottom: 60,
            left: 80,
        };
        const rowHeight = 30;

        const yLabels = [...new Set(data.map((d) => d.row.split(' - ')[0]))];

        const height = margin.top + margin.bottom + rowHeight * yLabels.length;

        const width = containerWidth;
        const innerWidth = width - margin.left - margin.right;
        const innerHeight = height - margin.top - margin.bottom;

        const xLabels =
            columns && columns.length ? columns : [...new Set(data.map((d) => d.column))];

        const x = d3.scaleBand().domain(xLabels).range([0, innerWidth]).padding(0.05);
        const y = d3.scaleBand().domain(yLabels).range([0, innerHeight]).padding(0.05);

        const valueExtent = d3.extent(data, (d) => d.value);
        const colorDomain = [...valueExtent].reverse();
        const colorScale = d3.scaleSequential(d3.interpolateOrRd).domain(colorDomain);

        const svg = d3.select(svgRef.current);
        svg.selectAll('*').remove();
        const tooltip = d3.select(tooltipRef.current);

        const chart = svg
            .attr('width', width)
            .attr('height', height)
            .append('g')
            .attr('transform', `translate(${margin.left},${margin.top})`);

        chart
            .selectAll('rect')
            .data(data)
            .join('rect')
            .attr('x', (d) => x(d.column))
            .attr('y', (d) => y(d.row.split(' - ')[0]))
            .attr('width', x.bandwidth())
            .attr('height', y.bandwidth())
            .attr('fill', (d) => d.color || colorScale(d.value))
            .attr('stroke', '#fff')
            .attr('stroke-width', 0.5)
            .style('cursor', 'pointer')
            .on('mouseover', (event, d) => {
                const containerRect = containerRef.current.getBoundingClientRect();
                tooltip
                    .style('opacity', 1)
                    .html(
                        `
        <strong>Value:</strong> ${d.value}<br/>
        <strong>MDRM:</strong> ${d.mdrm}<br/>
        <strong>Schedule:</strong> ${d.schedule}
      `,
                    );
                positionTooltip(event);
            })
            .on('mousemove', (event) => {
                positionTooltip(event);
            })
            .on('mouseout', () => tooltip.style('opacity', 0));

        chart
            .selectAll('text.cell-value')
            .data(data)
            .join('text')
            .attr('class', 'cell-value')
            .attr('x', (d) => x(d.column) + x.bandwidth() / 2)
            .attr('y', (d) => y(d.row.split(' - ')[0]) + y.bandwidth() / 2)
            .text((d) => d.value)
            .attr('text-anchor', 'middle')
            .attr('dominant-baseline', 'central')
            .style('fill', '#000')
            .style('font-size', '10px')
            .style('font-weight', 'bold');

        chart
            .append('g')
            .call(d3.axisLeft(y).tickSize(0))
            .selectAll('text')
            .style('font-weight', 'bold')
            .style('font-size', '12px');

        chart
            .append('g')
            .attr('transform', `translate(0,${innerHeight})`)
            .call(d3.axisBottom(x).tickSize(0).tickFormat(formatQuarterLabel))
            .selectAll('text')
            .style('font-size', '12px')
            .style('font-weight', 'bold')
            .style('text-anchor', 'end')
            .attr('transform', 'rotate(-45)');
    }, [data, columns]);

    return (
        <div ref={containerRef} className="heatmap-container">
            <svg ref={svgRef} />
            <div ref={tooltipRef} className="heatmap-tooltip" />
            <div className="heatmap-note">
                Note: The color temperature is indicative of the normalized values, and
                not the actual value.
            </div>
        </div>
    );
};

export default HeatmapChart;
